import { StargraveAdvancementChoice, StargraveCrewData, StargraveLeader, StargraveLeaderRole, StargraveLootResolution, StargraveMemberGameState } from '../types';
import { loadWarbandData, saveWarbandData } from './localStorageService';
import { queueSyncEntity } from './syncCoordinator';
import { buildStargraveSoldier, RecruitStargraveSoldierInput } from '../game/stargraveSoldierRules';
import { getAllStargraveSoldiers } from '../stores/stargraveCustomStore';
import { stargraveAdvancedTechnology } from '../data/stargrave/advancedTechnology';
import { stargraveShipUpgrades } from '../data/stargrave/shipUpgrades';
import { stargraveAdvancedWeapons } from '../data/stargrave/advancedWeapons';
import { stargraveAlienArtefacts } from '../data/stargrave/alienArtefacts';
import { applyStargraveDamage, applyStargraveHealing } from '../game/stargravePlayRules';
import { addStargraveBuff, removeStargraveBuff } from '../game/stargraveBuffRules';
import {
  canAddPermanentInjury,
  getEffectiveLeaderStats,
  StargraveSurvivalResolution,
  advanceStargraveLeader,
  canAdvanceStargraveLeader,
} from '../game/stargraveCampaignRules';
import { stargravePermanentInjuries } from '../data/stargrave/permanentInjuries';
import { isStargraveLootResolutionComplete, resolveStargraveLoot } from '../game/stargraveLootRules';
import { getStargraveUpkeepSuits, getStargraveUpkeepWaiverLimit, getArmouryBoostableWeapons, StargraveArmourUpkeepOptions } from '../game/stargraveUpkeepRules';

const emptyCrew: StargraveCrewData = {
  captain: null,
  firstMate: null,
  soldiers: [],
  advancedTechnology: [],
  advancedWeapons: [],
  alienArtefacts: [],
  secrets: 0,
  ship: { name: 'Unnamed Ship', upgradeIds: [] },
};

function updateMemberGameState(
  crew: StargraveCrewData,
  memberId: string,
  updater: (state: StargraveMemberGameState, maximumHealth: number) => StargraveMemberGameState
): StargraveCrewData {
  let found = false;
  const updateLeader = (leader: StargraveLeader | null) => {
    if (!leader || leader.id !== memberId) return leader;
    found = true;
    return { ...leader, gameState: updater(leader.gameState, leader.stats.health) };
  };
  const captain = updateLeader(crew.captain);
  const firstMate = updateLeader(crew.firstMate);
  const soldiers = crew.soldiers.map((soldier) => {
    if (soldier.id !== memberId) return soldier;
    found = true;
    const maximumHealth = getAllStargraveSoldiers().find((profile) => profile.id === soldier.profileId)?.health ?? soldier.gameState.currentHealth;
    return { ...soldier, gameState: updater(soldier.gameState, maximumHealth) };
  });
  if (!found) throw new Error('Crew member not found.');
  return { ...crew, captain, firstMate, soldiers };
}

export const stargraveCrewService = {
  getWarband(warbandId: string) {
    return loadWarbandData(warbandId)?.warband ?? null;
  },

  get(warbandId: string): StargraveCrewData {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') return emptyCrew;
    return stored.stargrave ?? emptyCrew;
  },

  saveLeader(warbandId: string, leader: StargraveLeader): StargraveCrewData {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') {
      throw new Error('Stargrave crew not found.');
    }

    const current = stored.stargrave ?? emptyCrew;
    const stargrave: StargraveCrewData = leader.role === 'captain'
      ? { ...current, captain: leader }
      : { ...current, firstMate: leader };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  updateLeader(warbandId: string, leader: StargraveLeader): StargraveCrewData {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const existing = leader.role === 'captain' ? current.captain : current.firstMate;
    if (!existing || existing.id !== leader.id) throw new Error('Stargrave leader not found.');
    const updated: StargraveLeader = {
      ...leader,
      id: existing.id,
      level: existing.level,
      permanentInjuryIds: existing.permanentInjuryIds,
      gameState: {
        ...existing.gameState,
        currentHealth: Math.min(existing.gameState.currentHealth, leader.stats.health),
      },
    };
    const stargrave = leader.role === 'captain'
      ? { ...current, captain: updated }
      : { ...current, firstMate: updated };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeLeaderPermanentInjury(warbandId: string, leaderId: string, injuryIndex: number): StargraveCrewData {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const role = current.captain?.id === leaderId ? 'captain' : current.firstMate?.id === leaderId ? 'firstMate' : null;
    const leader = role === 'captain' ? current.captain : role === 'firstMate' ? current.firstMate : null;
    if (!leader) throw new Error('Stargrave leader not found.');
    if (injuryIndex < 0 || injuryIndex >= leader.permanentInjuryIds.length) throw new Error('Permanent injury not found.');
    const updatedLeader = { ...leader, permanentInjuryIds: leader.permanentInjuryIds.filter((_, index) => index !== injuryIndex) };
    const stargrave = role === 'captain'
      ? { ...current, captain: updatedLeader }
      : { ...current, firstMate: updatedLeader };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  addLeaderPermanentInjury(warbandId: string, leaderId: string, injuryId: string): StargraveCrewData {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const role = current.captain?.id === leaderId ? 'captain' : current.firstMate?.id === leaderId ? 'firstMate' : null;
    const leader = role === 'captain' ? current.captain : role === 'firstMate' ? current.firstMate : null;
    if (!leader) throw new Error('Stargrave leader not found.');
    const injury = stargravePermanentInjuries.find((candidate) => candidate.id === injuryId);
    if (!injury || !canAddPermanentInjury(leader.permanentInjuryIds, injury)) throw new Error('Permanent injury cannot be added.');
    const updatedLeader = { ...leader, permanentInjuryIds: [...leader.permanentInjuryIds, injury.id] };
    const stargrave = role === 'captain'
      ? { ...current, captain: updatedLeader }
      : { ...current, firstMate: updatedLeader };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  /** Applies pre-game injury effects (Never Quite As Strong, Lingering Injury, Badly Wounded), treatment payments and combat armour upkeep. */
  startGame(warbandId: string, options: {
    lingeringPayments?: Record<string, boolean>;
    badlyWoundedPayments?: Record<string, boolean>;
    armourUpkeep?: StargraveArmourUpkeepOptions;
    /** Weapon Locker upgrade: one soldier carries a +1 Damage weapon this game. */
    weaponLocker?: { soldierId: string };
    /** Buffs from resolved Out of Game (B) powers, with any Strain damage. */
    powerBuffs?: Array<{ buffId: string; memberId: string; strainDamage?: number }>;
    /** Re-wire Robot: permanent +1 stat / -1 Will, once per robot. */
    robotRewires?: Array<{ soldierId: string; stat: 'move' | 'fight' | 'armour' }>;
  } = {}) {
    const { lingeringPayments = {}, badlyWoundedPayments = {}, armourUpkeep, weaponLocker, powerBuffs = [], robotRewires = [] } = options;
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const hasMedic = current.soldiers.some((soldier) => soldier.profileId === 'medic');
    let cost = 0;
    const prepareLeader = (leader: StargraveLeader | null) => {
      if (!leader) return leader;
      const neverQuiteAsStrong = leader.permanentInjuryIds.filter((id) => id === 'never-quite-as-strong').length;
      const lingering = leader.permanentInjuryIds.filter((id) => id === 'lingering-injury').length;
      const badlyWounded = Boolean(leader.gameState.badlyWounded);
      if (neverQuiteAsStrong === 0 && lingering === 0 && !badlyWounded) return leader;
      const maximumHealth = getEffectiveLeaderStats(leader).health;
      let startingHealth = maximumHealth;
      if (badlyWounded) {
        if (badlyWoundedPayments[leader.id]) cost += hasMedic ? 50 : 100;
        else startingHealth = Math.floor(maximumHealth / 2);
      }
      let penalty = neverQuiteAsStrong;
      if (lingering > 0) {
        if (lingeringPayments[leader.id]) cost += (lingering >= 2 ? 40 : 30) - (hasMedic ? 10 : 0);
        else penalty += lingering >= 2 ? 4 : 3;
      }
      return {
        ...leader,
        gameState: { ...leader.gameState, currentHealth: Math.max(1, startingHealth - penalty), badlyWounded: false },
      };
    };
    const stargrave = {
      ...current,
      captain: prepareLeader(current.captain),
      firstMate: prepareLeader(current.firstMate),
      // Alien Artefacts recharge at the start of every game
      alienArtefacts: current.alienArtefacts.map((instance) => {
        const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
        return artefact?.uses !== undefined ? { ...instance, remainingUses: artefact.uses } : instance;
      }),
    };

    // Combat armour upkeep: pay, waive (Armoury), field for free, or leave unpaid.
    const decisions = armourUpkeep?.decisions ?? {};
    const replacements = armourUpkeep?.replacements ?? {};
    const weaponBoosts = armourUpkeep?.weaponBoosts ?? [];
    const suits = getStargraveUpkeepSuits(current);
    const waived = suits.filter((suit) => decisions[suit.key] === 'waived');
    if (waived.length + weaponBoosts.length > getStargraveUpkeepWaiverLimit(current)) {
      throw new Error('The Armoury power cannot cover that many waivers and weapon boosts.');
    }
    for (const suit of suits) {
      const decision = decisions[suit.key] ?? 'pay';
      if (decision === 'pay') cost += suit.cost;
      if (decision !== 'unpaid') continue;
      const [kind, ownerId, gearId] = suit.key.split(':');
      if (kind === 'gear') {
        // Unpaid leader suit is left behind; it can be exchanged in manage mode.
        const stripGear = (leader: StargraveLeader | null) => leader && leader.id === ownerId
          ? { ...leader, gearIds: leader.gearIds.filter((id) => id !== gearId) }
          : leader;
        stargrave.captain = stripGear(stargrave.captain);
        stargrave.firstMate = stripGear(stargrave.firstMate);
      } else if (kind === 'tech') {
        // Unpaid advanced-tech suit returns to cargo.
        stargrave.advancedTechnology = current.advancedTechnology.map((instance) =>
          `tech:${instance.instanceId}` === suit.key ? { ...instance, ownerId: null } : instance);
      } else {
        // Unpaid soldier suit: the soldier sits this game out, optionally replaced by a temporary Recruit/Runner.
        stargrave.soldiers = (stargrave.soldiers ?? current.soldiers).map((soldier) =>
          soldier.id === ownerId ? { ...soldier, gameState: { ...soldier.gameState, sittingOut: true } } : soldier);
        const replacement = replacements[ownerId];
        if (replacement) {
          stargrave.soldiers = [...stargrave.soldiers, {
            ...buildStargraveSoldier({ profileId: replacement, isRobot: false, availableCredits: 0 }),
            isTemporary: true,
          }];
        }
      }
    }

    // Armoury weapon boosts: +1 Damage buff for this game only (cleared post-game).
    if (weaponBoosts.length > 0) {
      const benchedSoldierIds = new Set(suits
        .filter((suit) => suit.key.startsWith('soldier:') && decisions[suit.key] === 'unpaid')
        .map((suit) => suit.ownerId));
      const eligible = getArmouryBoostableWeapons(current)
        .filter((option) => !benchedSoldierIds.has(option.memberId));
      const applyBoost = <T extends { id: string; gameState: StargraveMemberGameState }>(member: T, memberId: string): T =>
        member.id === memberId
          ? { ...member, gameState: { ...member.gameState, activeBuffs: addStargraveBuff(member.gameState.activeBuffs, 'armoury-weapon-boost') } }
          : member;
      for (const boost of weaponBoosts) {
        if (!eligible.some((option) => option.memberId === boost.memberId && option.weaponId === boost.weaponId)) {
          throw new Error('Select a standard pistol, carbine, or shotgun for the Armoury boost.');
        }
        if (stargrave.captain) stargrave.captain = applyBoost(stargrave.captain, boost.memberId);
        if (stargrave.firstMate) stargrave.firstMate = applyBoost(stargrave.firstMate, boost.memberId);
        stargrave.soldiers = stargrave.soldiers.map((soldier) => applyBoost(soldier, boost.memberId));
      }
    }

    // Weapon Locker: one fielded soldier carries a +1 Damage weapon for this game.
    if (weaponLocker) {
      if (!current.ship.upgradeIds.includes('weapon-locker')) throw new Error('The crew does not own a Weapon Locker.');
      const benched = suits.some((suit) =>
        suit.key.startsWith('soldier:') && suit.ownerId === weaponLocker.soldierId && decisions[suit.key] === 'unpaid');
      const soldier = current.soldiers.find((candidate) => candidate.id === weaponLocker.soldierId);
      if (!soldier || soldier.storedInShip || benched) throw new Error('Select a fielded soldier for the Weapon Locker.');
      stargrave.soldiers = stargrave.soldiers.map((candidate) =>
        candidate.id === weaponLocker.soldierId
          ? { ...candidate, gameState: { ...candidate.gameState, activeBuffs: addStargraveBuff(candidate.gameState.activeBuffs, 'weapon-locker-boost') } }
          : candidate);
    }

    // Buffs from resolved pre-game powers (e.g. Armour Plates), with Strain applied as starting damage.
    for (const grant of powerBuffs) {
      const isLeader = [current.captain, current.firstMate].some((leader) => leader?.id === grant.memberId);
      const grantSoldier = current.soldiers.find((candidate) => candidate.id === grant.memberId);
      if (!isLeader && (!grantSoldier || grantSoldier.storedInShip)) throw new Error('Select a fielded member for the power buff.');
      const applyGrant = <T extends { id: string; gameState: StargraveMemberGameState }>(member: T): T =>
        member.id === grant.memberId
          ? {
              ...member,
              gameState: {
                ...member.gameState,
                activeBuffs: addStargraveBuff(member.gameState.activeBuffs, grant.buffId),
                currentHealth: Math.max(1, member.gameState.currentHealth - (grant.strainDamage ?? 0)),
              },
            }
          : member;
      if (stargrave.captain) stargrave.captain = applyGrant(stargrave.captain);
      if (stargrave.firstMate) stargrave.firstMate = applyGrant(stargrave.firstMate);
      stargrave.soldiers = stargrave.soldiers.map((candidate) => applyGrant(candidate));
    }

    // Re-wire Robot: permanent enhancement, one per robot ever.
    const rewiredIds = new Set<string>();
    for (const rewire of robotRewires) {
      const robot = current.soldiers.find((candidate) => candidate.id === rewire.soldierId);
      if (!robot?.isRobot) throw new Error('Select a robot soldier to re-wire.');
      if (robot.rewiredStat || rewiredIds.has(robot.id)) throw new Error(`${robot.name} has already been re-wired.`);
      rewiredIds.add(robot.id);
      stargrave.soldiers = stargrave.soldiers.map((candidate) =>
        candidate.id === rewire.soldierId ? { ...candidate, rewiredStat: rewire.stat } : candidate);
    }

    if (cost > stored.warband.gold) throw new Error('Not enough credits for injury treatments and armour upkeep.');
    const warband = { ...stored.warband, gold: stored.warband.gold - cost };
    saveWarbandData(warbandId, { ...stored, warband, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  recruitSoldier(warbandId: string, input: Omit<RecruitStargraveSoldierInput, 'availableCredits'>) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') {
      throw new Error('Stargrave crew not found.');
    }
    const soldier = buildStargraveSoldier({ ...input, availableCredits: stored.warband.gold });
    const profile = getAllStargraveSoldiers().find((candidate) => candidate.id === soldier.profileId)!;
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = { ...current, soldiers: [...current.soldiers, soldier] };
    const warband = { ...stored.warband, gold: stored.warband.gold - profile.cost };
    saveWarbandData(warbandId, { ...stored, warband, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  /** Free temporary soldier for one game; removed automatically in post-game. */
  recruitTemporarySoldier(warbandId: string, input: Omit<RecruitStargraveSoldierInput, 'availableCredits'>) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const soldier = {
      ...buildStargraveSoldier({ ...input, availableCredits: Number.MAX_SAFE_INTEGER }),
      isTemporary: true,
    };
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = { ...current, soldiers: [...current.soldiers, soldier] };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  updateSoldier(warbandId: string, soldierId: string, input: Omit<RecruitStargraveSoldierInput, 'availableCredits'>) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const existing = current.soldiers.find((soldier) => soldier.id === soldierId);
    if (!existing) throw new Error('Stargrave soldier not found.');
    const oldProfile = getAllStargraveSoldiers().find((profile) => profile.id === existing.profileId);
    const availableCredits = stored.warband.gold + (oldProfile?.cost ?? 0);
    const replacement = buildStargraveSoldier({ ...input, availableCredits });
    const newProfile = getAllStargraveSoldiers().find((profile) => profile.id === replacement.profileId)!;
    const updatedSoldier = {
      ...replacement,
      id: existing.id,
      gameState: existing.gameState,
      storedInShip: existing.storedInShip,
      isTemporary: existing.isTemporary,
      rewiredStat: existing.rewiredStat,
      driveImageId: existing.driveImageId,
    };
    const stargrave = {
      ...current,
      soldiers: current.soldiers.map((soldier) => soldier.id === soldierId ? updatedSoldier : soldier),
      advancedWeapons: current.advancedWeapons.map((instance) => {
        if (instance.ownerId !== soldierId) return instance;
        const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
        return weapon && newProfile.equipmentIds.includes(weapon.baseWeaponId)
          ? instance
          : { ...instance, ownerId: null };
      }),
    };
    const warband = { ...stored.warband, gold: stored.warband.gold + (oldProfile?.cost ?? 0) - newProfile.cost };
    saveWarbandData(warbandId, { ...stored, warband, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  setSoldierStoredInShip(warbandId: string, soldierId: string, storedInShip: boolean) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    if (!current.soldiers.some((soldier) => soldier.id === soldierId)) throw new Error('Stargrave soldier not found.');
    const stargrave = {
      ...current,
      soldiers: current.soldiers.map((soldier) =>
        soldier.id === soldierId ? { ...soldier, storedInShip } : soldier),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  addTechnology(warbandId: string, technologyId: string, addForFree = false) {
    const stored = loadWarbandData(warbandId);
    const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === technologyId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!technology) throw new Error('Advanced Technology not found.');
    if (!addForFree && stored.warband.gold < technology.cost) throw new Error('Not enough credits.');

    const current = stored.stargrave ?? emptyCrew;
    const instance = {
      instanceId: crypto.randomUUID(),
      technologyId,
      ownerId: null,
      ...(technology.uses ? { remainingUses: technology.uses } : {}),
    };
    const stargrave = { ...current, advancedTechnology: [...current.advancedTechnology, instance] };
    const warband = { ...stored.warband, gold: stored.warband.gold - (addForFree ? 0 : technology.cost) };
    saveWarbandData(warbandId, { ...stored, warband, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  assignTechnology(warbandId: string, instanceId: string, ownerId: string | null) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const validOwnerIds = new Set([
      current.captain?.id,
      current.firstMate?.id,
      ...current.soldiers.map((soldier) => soldier.id),
    ].filter((id): id is string => Boolean(id)));
    if (ownerId && !validOwnerIds.has(ownerId)) throw new Error('Crew member not found.');
    if (!current.advancedTechnology.some((instance) => instance.instanceId === instanceId)) {
      throw new Error('Advanced Technology instance not found.');
    }
    const stargrave = {
      ...current,
      advancedTechnology: current.advancedTechnology.map((instance) =>
        instance.instanceId === instanceId ? { ...instance, ownerId } : instance
      ),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeTechnology(warbandId: string, instanceId: string, sell = false) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.advancedTechnology.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Advanced Technology instance not found.');
    const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
    const stargrave = {
      ...current,
      advancedTechnology: current.advancedTechnology.filter((candidate) => candidate.instanceId !== instanceId),
    };
    const warband = {
      ...stored.warband,
      gold: stored.warband.gold + (sell ? technology?.salePrice ?? 0 : 0),
    };
    saveWarbandData(warbandId, { ...stored, warband, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  addAdvancedWeapon(warbandId: string, weaponId: string, addForFree = false) {
    const stored = loadWarbandData(warbandId);
    const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === weaponId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!weapon) throw new Error('Advanced Weapon not found.');
    if (!addForFree && stored.warband.gold < weapon.cost) throw new Error('Not enough credits.');
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = {
      ...current,
      advancedWeapons: [...current.advancedWeapons, { instanceId: crypto.randomUUID(), weaponId, ownerId: null }],
    };
    const warband = { ...stored.warband, gold: stored.warband.gold - (addForFree ? 0 : weapon.cost) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  assignAdvancedWeapon(warbandId: string, instanceId: string, ownerId: string | null) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.advancedWeapons.find((candidate) => candidate.instanceId === instanceId);
    const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance?.weaponId);
    if (!instance || !weapon) throw new Error('Advanced Weapon instance not found.');
    if (ownerId) {
      const leader = [current.captain, current.firstMate].find((candidate) => candidate?.id === ownerId);
      const soldier = current.soldiers.find((candidate) => candidate.id === ownerId);
      const ownerWeaponIds = leader?.gearIds
        ?? getAllStargraveSoldiers().find((profile) => profile.id === soldier?.profileId)?.equipmentIds
        ?? [];
      if (!ownerWeaponIds.includes(weapon.baseWeaponId)) {
        throw new Error(`The selected crew member does not carry a ${weapon.baseWeaponId}.`);
      }
    }
    const stargrave = {
      ...current,
      advancedWeapons: current.advancedWeapons.map((candidate) =>
        candidate.instanceId === instanceId ? { ...candidate, ownerId } : candidate
      ),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeAdvancedWeapon(warbandId: string, instanceId: string, sell = false) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.advancedWeapons.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Advanced Weapon instance not found.');
    const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
    const stargrave = {
      ...current,
      advancedWeapons: current.advancedWeapons.filter((candidate) => candidate.instanceId !== instanceId),
    };
    const warband = { ...stored.warband, gold: stored.warband.gold + (sell ? weapon?.salePrice ?? 0 : 0) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  addAlienArtefact(warbandId: string, artefactId: string, addForFree = false) {
    const stored = loadWarbandData(warbandId);
    const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === artefactId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!artefact) throw new Error('Alien Artefact not found.');
    if (!addForFree && stored.warband.gold < artefact.cost) throw new Error('Not enough credits.');
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = {
      ...current,
      alienArtefacts: [...current.alienArtefacts, { instanceId: crypto.randomUUID(), artefactId, ownerId: null, remainingUses: artefact.uses }],
    };
    const warband = { ...stored.warband, gold: stored.warband.gold - (addForFree ? 0 : artefact.cost) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  useAlienArtefact(warbandId: string, instanceId: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.alienArtefacts.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Alien Artefact instance not found.');
    const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
    if (!artefact || artefact.uses === undefined) throw new Error('This artefact does not track uses.');
    const remaining = instance.remainingUses ?? artefact.uses;
    if (remaining <= 0) throw new Error('This artefact has no uses remaining this game.');
    const stargrave = {
      ...current,
      alienArtefacts: current.alienArtefacts.map((candidate) =>
        candidate.instanceId === instanceId ? { ...candidate, remainingUses: remaining - 1 } : candidate
      ),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  assignAlienArtefact(warbandId: string, instanceId: string, ownerId: string | null) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.alienArtefacts.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Alien Artefact instance not found.');
    if (ownerId) {
      if (![current.captain?.id, current.firstMate?.id].includes(ownerId)) {
        throw new Error('Alien Artefacts may only be carried by the Captain or First Mate.');
      }
      const duplicate = current.alienArtefacts.some((candidate) =>
        candidate.instanceId !== instanceId && candidate.ownerId === ownerId && candidate.artefactId === instance.artefactId
      );
      if (duplicate) throw new Error('A figure may not carry more than one of each Alien Artefact.');
    }
    const stargrave = {
      ...current,
      alienArtefacts: current.alienArtefacts.map((candidate) =>
        candidate.instanceId === instanceId ? { ...candidate, ownerId } : candidate
      ),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeAlienArtefact(warbandId: string, instanceId: string, sell = false) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.alienArtefacts.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Alien Artefact instance not found.');
    const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
    const stargrave = {
      ...current,
      alienArtefacts: current.alienArtefacts.filter((candidate) => candidate.instanceId !== instanceId),
    };
    const warband = { ...stored.warband, gold: stored.warband.gold + (sell ? artefact?.salePrice ?? 0 : 0) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  removeShipUpgrade(warbandId: string, upgradeIndex: number, refund = false) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const upgradeId = current.ship.upgradeIds[upgradeIndex];
    if (!upgradeId) throw new Error('Ship upgrade not found.');
    const upgrade = stargraveShipUpgrades.find((candidate) => candidate.id === upgradeId);
    const stargrave = {
      ...current,
      ship: { ...current.ship, upgradeIds: current.ship.upgradeIds.filter((_, index) => index !== upgradeIndex) },
    };
    const warband = { ...stored.warband, gold: stored.warband.gold + (refund ? upgrade?.cost ?? 0 : 0) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  useTechnology(warbandId: string, instanceId: string, options: { healTargetId?: string; healAmount?: number } = {}) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const instance = current.advancedTechnology.find((candidate) => candidate.instanceId === instanceId);
    if (!instance) throw new Error('Advanced Technology instance not found.');
    const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
    if (!technology) throw new Error('Advanced Technology not found.');
    if (instance.remainingUses !== undefined && instance.remainingUses <= 0) throw new Error('This item has no uses remaining.');

    let stargrave = current;

    if (technology.useBuffId) {
      if (!instance.ownerId) throw new Error('Assign an owner before using this item.');
      const ownerId = instance.ownerId;
      stargrave = updateMemberGameState(stargrave, ownerId, (state) => ({
        ...state,
        activeBuffs: addStargraveBuff(state.activeBuffs ?? [], technology.useBuffId!),
      }));
    }

    if (options.healTargetId && (options.healAmount ?? 0) > 0) {
      stargrave = updateMemberGameState(stargrave, options.healTargetId, (state, maximumHealth) =>
        applyStargraveHealing(state, options.healAmount!, maximumHealth)
      );
    }

    if (instance.remainingUses !== undefined) {
      const nextUses = instance.remainingUses - 1;
      const advancedTechnology = nextUses === 0
        ? stargrave.advancedTechnology.filter((candidate) => candidate.instanceId !== instanceId)
        : stargrave.advancedTechnology.map((candidate) =>
            candidate.instanceId === instanceId ? { ...candidate, remainingUses: nextUses } : candidate
          );
      stargrave = { ...stargrave, advancedTechnology };
    }

    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  renameShip(warbandId: string, name: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!name.trim()) throw new Error('Ship name is required.');
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = { ...current, ship: { ...current.ship, name: name.trim() } };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  purchaseShipUpgrade(warbandId: string, upgradeId: string, addForFree = false) {
    const stored = loadWarbandData(warbandId);
    const upgrade = stargraveShipUpgrades.find((candidate) => candidate.id === upgradeId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!upgrade) throw new Error('Ship upgrade not found.');
    const current = stored.stargrave ?? emptyCrew;
    const ownedCount = current.ship.upgradeIds.filter((id) => id === upgradeId).length;
    if (ownedCount >= upgrade.maximum) throw new Error('This ship cannot install another copy of that upgrade.');
    if (!addForFree && stored.warband.gold < upgrade.cost) throw new Error('Not enough credits.');

    const stargrave = {
      ...current,
      ship: { ...current.ship, upgradeIds: [...current.ship.upgradeIds, upgradeId] },
    };
    const warband = { ...stored.warband, gold: stored.warband.gold - (addForFree ? 0 : upgrade.cost) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  renameMember(warbandId: string, memberId: string, name: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (!name.trim()) throw new Error('Name is required.');
    const current = stored.stargrave ?? emptyCrew;
    const isCaptain = current.captain?.id === memberId;
    const isFirstMate = current.firstMate?.id === memberId;
    const isSoldier = current.soldiers.some((soldier) => soldier.id === memberId);
    if (!isCaptain && !isFirstMate && !isSoldier) throw new Error('Crew member not found.');

    const stargrave: StargraveCrewData = {
      ...current,
      captain: isCaptain && current.captain ? { ...current.captain, name: name.trim() } : current.captain,
      firstMate: isFirstMate && current.firstMate ? { ...current.firstMate, name: name.trim() } : current.firstMate,
      soldiers: current.soldiers.map((soldier) => soldier.id === memberId ? { ...soldier, name: name.trim() } : soldier),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeMember(warbandId: string, memberId: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const isCaptain = current.captain?.id === memberId;
    const isFirstMate = current.firstMate?.id === memberId;
    const isSoldier = current.soldiers.some((soldier) => soldier.id === memberId);
    if (!isCaptain && !isFirstMate && !isSoldier) throw new Error('Crew member not found.');

    const stargrave: StargraveCrewData = {
      ...current,
      captain: isCaptain ? null : current.captain,
      firstMate: isFirstMate ? null : current.firstMate,
      soldiers: current.soldiers.filter((soldier) => soldier.id !== memberId),
      advancedTechnology: current.advancedTechnology.map((instance) =>
        instance.ownerId === memberId ? { ...instance, ownerId: null } : instance
      ),
      advancedWeapons: current.advancedWeapons.map((instance) =>
        instance.ownerId === memberId ? { ...instance, ownerId: null } : instance
      ),
      alienArtefacts: current.alienArtefacts.map((instance) =>
        instance.ownerId === memberId ? { ...instance, ownerId: null } : instance
      ),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  updateMemberGameState(warbandId: string, memberId: string, changes: Partial<StargraveMemberGameState>) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const stargrave = updateMemberGameState(current, memberId, (state, maximumHealth) => ({
      ...state,
      ...changes,
      currentHealth: Math.max(0, Math.min(changes.currentHealth ?? state.currentHealth, maximumHealth)),
    }));
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  applyDamage(warbandId: string, memberId: string, amount: number) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const stargrave = updateMemberGameState(stored.stargrave ?? emptyCrew, memberId, (state) => applyStargraveDamage(state, amount));
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  addMemberBuff(warbandId: string, memberId: string, buffId: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const stargrave = updateMemberGameState(stored.stargrave ?? emptyCrew, memberId, (state) => ({
      ...state,
      activeBuffs: addStargraveBuff(state.activeBuffs ?? [], buffId),
    }));
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  removeMemberBuff(warbandId: string, memberId: string, buffId: string, count: number | 'all' = 'all') {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const stargrave = updateMemberGameState(stored.stargrave ?? emptyCrew, memberId, (state) => ({
      ...state,
      activeBuffs: removeStargraveBuff(state.activeBuffs ?? [], buffId, count),
    }));
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  applyHealing(warbandId: string, memberId: string, amount: number) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const stargrave = updateMemberGameState(stored.stargrave ?? emptyCrew, memberId, (state, maximumHealth) =>
      applyStargraveHealing(state, amount, maximumHealth)
    );
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  resetGame(warbandId: string) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const resetState = (state: StargraveMemberGameState, maximumHealth: number): StargraveMemberGameState => ({
      ...state,
      currentHealth: maximumHealth,
      stunned: false,
      poisoned: false,
      loot: null,
      leftTable: false,
      activeBuffs: [],
    });
    const stargrave: StargraveCrewData = {
      ...current,
      captain: current.captain ? { ...current.captain, gameState: resetState(current.captain.gameState, current.captain.stats.health) } : null,
      firstMate: current.firstMate ? { ...current.firstMate, gameState: resetState(current.firstMate.gameState, current.firstMate.stats.health) } : null,
      soldiers: current.soldiers.map((soldier) => {
        const maximumHealth = getAllStargraveSoldiers().find((profile) => profile.id === soldier.profileId)?.health ?? soldier.gameState.currentHealth;
        return { ...soldier, gameState: resetState(soldier.gameState, maximumHealth) };
      }),
    };
    saveWarbandData(warbandId, { ...stored, stargrave });
    queueSyncEntity(`warband:${warbandId}`);
    return stargrave;
  },

  completePostGame(
    warbandId: string,
    resolutions: StargraveSurvivalResolution[],
    experienceGained = 0,
    creditsGained = 0,
    lootResolutions: StargraveLootResolution[] = [],
    /** Free robot soldiers from successful Create Robot powers. */
    freeRobots: Array<{ profileId: string; name?: string }> = []
  ) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    const knockedOutIds = [
      ...(current.captain?.gameState.currentHealth === 0 ? [current.captain.id] : []),
      ...(current.firstMate?.gameState.currentHealth === 0 ? [current.firstMate.id] : []),
      ...current.soldiers.filter((soldier) => !soldier.isTemporary && soldier.gameState.currentHealth === 0).map((soldier) => soldier.id),
    ];
    const resolutionMap = new Map(resolutions.map((resolution) => [resolution.memberId, resolution]));
    if (knockedOutIds.some((id) => !resolutionMap.has(id))) throw new Error('Resolve every knocked-out crew member.');

    const securedLoot = [
      ...(current.captain?.gameState.leftTable && current.captain.gameState.currentHealth > 0 && current.captain.gameState.loot
        ? [{ memberId: current.captain.id, lootType: current.captain.gameState.loot }] : []),
      ...(current.firstMate?.gameState.leftTable && current.firstMate.gameState.currentHealth > 0 && current.firstMate.gameState.loot
        ? [{ memberId: current.firstMate.id, lootType: current.firstMate.gameState.loot }] : []),
      ...current.soldiers.filter((soldier) => soldier.gameState.leftTable && soldier.gameState.currentHealth > 0 && soldier.gameState.loot)
        .map((soldier) => ({ memberId: soldier.id, lootType: soldier.gameState.loot! })),
    ];
    const lootResolutionMap = new Map(lootResolutions.map((resolution) => [resolution.memberId, resolution]));
    if (securedLoot.some(({ memberId, lootType }) => {
      const resolution = lootResolutionMap.get(memberId);
      return !resolution || resolution.lootType !== lootType || !isStargraveLootResolutionComplete(resolution);
    })) throw new Error('Resolve every secured loot token.');

    const hasMedic = current.soldiers.some((soldier) => soldier.profileId === 'medic');
    const removedIds = new Set<string>();
    const closeCallIds = new Set<string>();

    const clearState = (state: StargraveMemberGameState, health: number): StargraveMemberGameState => ({
      ...state,
      currentHealth: health,
      stunned: false,
      poisoned: false,
      loot: null,
      leftTable: false,
      activeBuffs: [],
      badlyWounded: false,
      sittingOut: false,
    });

    const resolveLeader = (leader: StargraveLeader | null): StargraveLeader | null => {
      if (!leader) return null;
      if (leader.gameState.currentHealth > 0) return { ...leader, gameState: clearState(leader.gameState, getEffectiveLeaderStats(leader).health) };
      const resolution = resolutionMap.get(leader.id)!;
      const outcome = resolution.outcome;
      if (!['dead', 'permanentInjury', 'badlyWounded', 'closeCall', 'fullRecovery'].includes(outcome)) {
        throw new Error('Select a valid leader survival outcome.');
      }
      if (outcome === 'dead') {
        removedIds.add(leader.id);
        return null;
      }
      if (outcome === 'permanentInjury') {
        const injury = stargravePermanentInjuries.find((candidate) => candidate.id === resolution.injuryId);
        if (!injury || !canAddPermanentInjury(leader.permanentInjuryIds, injury)) {
          throw new Error('Select a valid permanent injury result.');
        }
        if (injury.id === 'lost-eye' && leader.permanentInjuryIds.includes('lost-eye')) {
          removedIds.add(leader.id);
          return null;
        }
        const updated = { ...leader, permanentInjuryIds: [...leader.permanentInjuryIds, injury.id] };
        return { ...updated, gameState: clearState(updated.gameState, getEffectiveLeaderStats(updated).health) };
      }
      const maximumHealth = getEffectiveLeaderStats(leader).health;
      if (outcome === 'badlyWounded') {
        // Treatment payment is decided in the next pre-game
        return { ...leader, gameState: { ...clearState(leader.gameState, Math.floor(maximumHealth / 2)), badlyWounded: true } };
      }
      if (outcome === 'closeCall') closeCallIds.add(leader.id);
      return { ...leader, gameState: clearState(leader.gameState, maximumHealth) };
    };

    const captain = resolveLeader(current.captain);
    const firstMate = resolveLeader(current.firstMate);
    const soldiers = current.soldiers.flatMap((soldier) => {
      // Temporary replacements leave the crew after the game.
      if (soldier.isTemporary) {
        removedIds.add(soldier.id);
        return [];
      }
      const profile = getAllStargraveSoldiers().find((candidate) => candidate.id === soldier.profileId);
      const maximumHealth = profile?.health ?? soldier.gameState.currentHealth;
      if (soldier.gameState.currentHealth > 0) return [{ ...soldier, gameState: clearState(soldier.gameState, maximumHealth) }];
      const outcome = resolutionMap.get(soldier.id)!.outcome;
      if (!['dead', 'badlyWounded', 'fullRecovery'].includes(outcome)) {
        throw new Error('Select a valid soldier survival outcome.');
      }
      if (outcome === 'dead') {
        removedIds.add(soldier.id);
        return [];
      }
      return [{
        ...soldier,
        gameState: clearState(soldier.gameState, outcome === 'badlyWounded' ? Math.floor(maximumHealth / 2) : maximumHealth),
      }];
    });
    for (const recruit of freeRobots) {
      if (recruit.profileId === 'armoured-trooper') throw new Error('Create Robot cannot add an Armoured Trooper.');
      soldiers.push(buildStargraveSoldier({
        profileId: recruit.profileId,
        name: recruit.name,
        isRobot: true,
        availableCredits: Number.MAX_SAFE_INTEGER,
      }));
    }
    const advancedTechnology = current.advancedTechnology.filter((instance) =>
      !instance.ownerId || (!removedIds.has(instance.ownerId) && !closeCallIds.has(instance.ownerId))
    );
    const advancedWeapons = current.advancedWeapons.filter((instance) =>
      !instance.ownerId || (!removedIds.has(instance.ownerId) && !closeCallIds.has(instance.ownerId))
    );
    const alienArtefacts = current.alienArtefacts.filter((instance) =>
      !instance.ownerId || (!removedIds.has(instance.ownerId) && !closeCallIds.has(instance.ownerId))
    );
    let lootCredits = 0;
    let lootExperience = 0;
    let secrets = current.secrets;
    const newAdvancedTechnology = [...advancedTechnology];
    const newAdvancedWeapons = [...advancedWeapons];
    const newAlienArtefacts = [...alienArtefacts];
    const hasCargoPods = current.ship.upgradeIds.includes('external-cargo-pods');
    for (const secured of securedLoot) {
      const resolution = lootResolutionMap.get(secured.memberId)!;
      const reward = resolveStargraveLoot(resolution);
      if (reward.kind === 'credits') lootCredits += reward.amount ?? 0;
      else if (reward.kind === 'tradeGoods') lootCredits += Math.round((reward.amount ?? 0) * (hasCargoPods ? 1.2 : 1));
      else if (reward.kind === 'information') {
        if (resolution.informationChoice === 'experience') lootExperience += 20;
        else lootCredits += reward.amount ?? 0;
      } else if (reward.kind === 'secret') {
        if (resolution.secretChoice === 'store') secrets += 1;
        else lootCredits += reward.amount ?? 0;
      } else if (reward.kind === 'advancedWeapon') {
        if (!stargraveAdvancedWeapons.some((item) => item.id === resolution.itemId)) throw new Error('Select a valid Advanced Weapon.');
        newAdvancedWeapons.push({ instanceId: crypto.randomUUID(), weaponId: resolution.itemId!, ownerId: null });
      } else if (reward.kind === 'advancedTechnology') {
        const item = stargraveAdvancedTechnology.find((candidate) => candidate.id === resolution.itemId && candidate.table === reward.technologyTable);
        if (!item) throw new Error('Select valid Advanced Technology from the indicated table.');
        newAdvancedTechnology.push({ instanceId: crypto.randomUUID(), technologyId: item.id, ownerId: null, ...(item.uses ? { remainingUses: item.uses } : {}) });
      } else if (reward.kind === 'alienArtefact') {
        if (!stargraveAlienArtefacts.some((item) => item.id === resolution.itemId)) throw new Error('Select a valid Alien Artefact.');
        newAlienArtefacts.push({ instanceId: crypto.randomUUID(), artefactId: resolution.itemId!, ownerId: null });
      }
    }
    const stargrave = {
      ...current,
      captain,
      firstMate,
      soldiers,
      advancedTechnology: newAdvancedTechnology,
      advancedWeapons: newAdvancedWeapons,
      alienArtefacts: newAlienArtefacts,
      secrets,
    };
    const warband = {
      ...stored.warband,
      gold: stored.warband.gold + Math.max(0, creditsGained) + lootCredits,
      experience: stored.warband.experience + Math.max(0, experienceGained) + lootExperience,
    };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  advanceLeader(warbandId: string, role: StargraveLeaderRole, choice: StargraveAdvancementChoice) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    if (stored.warband.experience < 100) throw new Error('At least 100 experience is required.');
    const current = stored.stargrave ?? emptyCrew;
    const leader = role === 'captain' ? current.captain : current.firstMate;
    if (!leader) throw new Error(`${role === 'captain' ? 'Captain' : 'First Mate'} not found.`);
    const captainLevel = current.captain?.level ?? 0;
    const firstMateLevel = current.firstMate?.level ?? 0;
    if (!canAdvanceStargraveLeader(role, captainLevel, firstMateLevel)) {
      throw new Error('This advancement would violate the Captain/First Mate level gap.');
    }
    const advanced = advanceStargraveLeader(leader, choice);
    const stargrave: StargraveCrewData = role === 'captain'
      ? { ...current, captain: advanced }
      : { ...current, firstMate: advanced };
    const warband = { ...stored.warband, experience: stored.warband.experience - 100 };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },

  spendSecret(
    warbandId: string,
    action: 'sell' | 'discard' | 'redeem',
    reward?: { kind: 'advancedWeapon' | 'advancedTechnology' | 'alienArtefact'; itemId: string }
  ) {
    const stored = loadWarbandData(warbandId);
    if (!stored || stored.warband.gameSystem !== 'stargrave') throw new Error('Stargrave crew not found.');
    const current = stored.stargrave ?? emptyCrew;
    if (current.secrets <= 0) throw new Error('No stored Secrets are available.');

    const advancedTechnology = [...current.advancedTechnology];
    const advancedWeapons = [...current.advancedWeapons];
    const alienArtefacts = [...current.alienArtefacts];
    if (action === 'redeem') {
      if (!reward) throw new Error('Select a Secret reward.');
      if (reward.kind === 'advancedWeapon') {
        if (!stargraveAdvancedWeapons.some((item) => item.id === reward.itemId)) throw new Error('Select a valid Advanced Weapon.');
        advancedWeapons.push({ instanceId: crypto.randomUUID(), weaponId: reward.itemId, ownerId: null });
      } else if (reward.kind === 'advancedTechnology') {
        const item = stargraveAdvancedTechnology.find((candidate) => candidate.id === reward.itemId);
        if (!item) throw new Error('Select valid Advanced Technology.');
        advancedTechnology.push({ instanceId: crypto.randomUUID(), technologyId: item.id, ownerId: null, ...(item.uses ? { remainingUses: item.uses } : {}) });
      } else {
        if (!stargraveAlienArtefacts.some((item) => item.id === reward.itemId)) throw new Error('Select a valid Alien Artefact.');
        alienArtefacts.push({ instanceId: crypto.randomUUID(), artefactId: reward.itemId, ownerId: null });
      }
    }
    const stargrave = {
      ...current,
      secrets: current.secrets - 1,
      advancedTechnology,
      advancedWeapons,
      alienArtefacts,
    };
    const warband = { ...stored.warband, gold: stored.warband.gold + (action === 'sell' ? 200 : 0) };
    saveWarbandData(warbandId, { ...stored, stargrave, warband });
    queueSyncEntity(`warband:${warbandId}`);
    return { stargrave, warband };
  },
};
